import { formatAmount } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { toast } from "@loot-os/ui/components/sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Link2 } from "lucide-react";
import { useState } from "react";

import { useCurrency } from "../lib/currency";
import { formatDate } from "../lib/format";
import { useTRPC } from "../lib/trpc";
import { useShopifyShop } from "../lib/useShopifyShop";
import { useTenantRole } from "../lib/useTenantRole";
import { ErrorAlert } from "./ErrorAlert";
import { ExpandableRow } from "./ExpandableRow";
import { ShopifyCustomerLink, ShopifyOrderLink } from "./ShopifyLinks";
import { ShopifyPurchaseDetail } from "./ShopifyPurchaseDetail";
import { TableMessageRow } from "./TableMessageRow";

/**
 * Shopify → Sales → Purchases: one row per box unit bought at Shopify checkout, newest first. A
 * row opens in place to the purchase's full detail.
 */
export function ShopifyPurchasesCard() {
  const trpc = useTRPC();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const currency = useCurrency();
  const { shopHandle } = useShopifyShop();
  const { can } = useTenantRole();
  const purchases = useQuery(trpc.shopify.purchases.queryOptions());
  const revealLink = useMutation(trpc.shopify.revealLink.mutationOptions());
  const [expanded, setExpanded] = useState<string | null>(null);
  const columns = can("admin") ? 8 : 7;

  // The clipboard item takes the pending link, so the copy stays inside the click's user
  // activation while the link is minted (Safari refuses a write made after an await).
  const copyRevealLink = (id: string) => {
    let minted = false;
    const url = revealLink.mutateAsync({ id }).then((r) => {
      minted = true;
      return new Blob([r.url], { type: "text/plain" });
    });
    navigator.clipboard
      .write([new ClipboardItem({ "text/plain": url })])
      .then(() => toast.success("Link copied. It signs the customer in for thirty days."))
      .catch(() => {
        // A failed mint is toasted by the mutation cache; only a refused clipboard lands here.
        if (minted) toast.error("The browser refused the clipboard.");
      });
  };

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle>Purchases</CardTitle>
        <CardDescription>
          One row per box unit bought at Shopify checkout, newest first.
          {can("admin") &&
            " Copy link gives a customer's box page again, signed in, for a shopper who lost theirs."}
        </CardDescription>
      </CardHeader>
      {/* A container, so an open row's detail can take the card's width and not the table's. */}
      <CardContent className="@container flex flex-col gap-2">
        {purchases.error && <ErrorAlert message={purchases.error.message} />}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Order</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Box</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Bought</TableHead>
              {can("admin") && <TableHead />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {(purchases.data ?? []).length === 0 && (
              <TableMessageRow colSpan={columns}>No purchases yet.</TableMessageRow>
            )}
            {(purchases.data ?? []).map((p) => (
              <ExpandableRow
                key={p.id}
                detailId={`shopify-purchase-${p.id}`}
                label={`Show ${p.orderName}`}
                isOpen={expanded === p.id}
                onToggle={() => setExpanded(expanded === p.id ? null : p.id)}
                colSpan={columns}
                detail={
                  <ShopifyPurchaseDetail
                    tenantId={tenantId}
                    purchaseId={p.id}
                    shopHandle={shopHandle}
                  />
                }
              >
                <TableCell>
                  <ShopifyOrderLink shopHandle={shopHandle} orderGid={p.orderGid}>
                    {p.orderName}
                  </ShopifyOrderLink>
                  {p.unitIndex > 0 ? ` · ${p.unitIndex + 1}` : ""}
                </TableCell>
                <TableCell>
                  <ShopifyCustomerLink tenantId={tenantId} row={p} />
                </TableCell>
                <TableCell>{p.boxName}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatAmount(p.paidPriceCents, currency)}
                </TableCell>
                <TableCell>
                  <Badge variant={p.status === "voided" ? "outline" : "secondary"}>
                    {p.soldOut ? "substituted: out of stock" : p.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">{formatDate(p.createdAt)}</TableCell>
                {can("admin") && (
                  <TableCell className="text-right">
                    {p.endUserId && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        disabled={revealLink.isPending && revealLink.variables?.id === p.id}
                        onClick={() => copyRevealLink(p.id)}
                      >
                        <Link2 />
                        Copy link
                      </Button>
                    )}
                  </TableCell>
                )}
              </ExpandableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
